import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "question-tab" }
const _hoisted_2 = { class: "title" }

import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  methodQuestionList,
  methodQuestionCreate,
  methodQuestionUpdate,
  methodQuestionDelete,
} from "@/common/api/rest/method/method-question";

import type {
  MethodQuestionPayloadType,
  MethodQuestionType,
} from "@/common/types/method/method-question";

import { questionForm } from "@/components";

import { baseConfirm, baseModal, baseTable } from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: 'method-question',
  props: {
    accountId: {},
    groupId: {},
    orders: {},
    remove: {},
    ordering: { type: Boolean }
  },
  emits: ["update:order"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { t, tm } = useI18n();

const listData = ref<MethodQuestionType[]>([]);

const selected = ref<MethodQuestionType | null>(null);
const questionPayload = ref<MethodQuestionPayloadType | null>(null);

const isEdit = ref(false);
const isLoading = ref(false);
const showQuestionAdd = ref(false);
const showQuestionDel = ref(false);

const tableHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
  { key: "type", label: t("common.labels.type") },
  { key: "required", label: t("common.labels.required") },
]);

const modalMessage = computed(() => {
  return isEdit.value
    ? t("question.texts.edit_question").toUpperCase()
    : t("question.texts.add_question").toUpperCase();
});

const delMessage = computed(() => {
  const message = t("question.texts.del_confirm", {
    concat: selected.value?.name,
  });
  const undo = t("common.texts.undo_message");

  return `${message}<br>${undo}`;
});

const questionTypes = computed(
  () => tm("enums.question_type") as Record<string, string>,
);

function sortData(data: MethodQuestionType[]) {
  return data?.sort((a, b) => {
    const indexA = props.orders!.indexOf(a.question_id);
    const indexB = props.orders!.indexOf(b.question_id);

    return indexA - indexB;
  });
}

function fetchData() {
  isLoading.value = true;

  methodQuestionList(props.accountId, props.groupId, {
    full: true,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        if (props.ordering && props.orders) {
          listData.value = sortData(data.list);
        } else {
          listData.value = data.list;
        }
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function add() {
  showQuestionAdd.value = true;
}

function addCancel() {
  isEdit.value = false;
  selected.value = null;
  questionPayload.value = null;
  showQuestionAdd.value = false;
}

function addConfirm() {
  if (!questionPayload.value) return;

  store.commit("SET_LOADING", true);

  methodQuestionCreate(props.accountId, props.groupId, questionPayload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        const newOrders = [...listData.value];
        newOrders.push(data);
        updateOrder(newOrders);

        fetchData();
        addCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function edit(data: MethodQuestionType) {
  questionPayload.value = data;
  selected.value = data;
  isEdit.value = true;
  showQuestionAdd.value = true;
}

function editConfirm() {
  if (!questionPayload.value) return;

  store.commit("SET_LOADING", true);

  methodQuestionUpdate(
    props.accountId,
    selected.value?.question_id as string,
    questionPayload.value,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        addCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch((error) => {
      console.log("error", error);
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function questionSubmit() {
  if (isEdit.value) editConfirm();
  else addConfirm();
}

function del(data: MethodQuestionType) {
  selected.value = data;
  showQuestionDel.value = true;
}

function delCancel() {
  selected.value = null;
  showQuestionDel.value = false;
}

function delConfirm() {
  store.commit("SET_LOADING", true);

  methodQuestionDelete(props.accountId, selected.value?.question_id as string)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        delCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updateOrder(data: MethodQuestionType[]) {
  const newOrder = data.map((item) => item.question_id);
  emit("update:order", newOrder);
}

onMounted(() => {
  fetchData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, _toDisplayString(_unref(t)("common.labels.questions")), 1),
    _createVNode(_unref(baseTable), {
      header: tableHeader.value,
      data: listData.value,
      loading: isLoading.value,
      options: true,
      edit: true,
      del: true,
      paginated: false,
      "show-search": false,
      ordering: __props.ordering,
      "onBtn:add": _cache[0] || (_cache[0] = ($event: any) => (add())),
      "onBtn:edit": _cache[1] || (_cache[1] = ($event: any) => (edit($event))),
      "onBtn:del": _cache[2] || (_cache[2] = ($event: any) => (del($event))),
      "onUpdate:order": updateOrder
    }, {
      type: _withCtx(({ data }) => [
        _createTextVNode(_toDisplayString(questionTypes.value[data.type]), 1)
      ]),
      required: _withCtx(({ data }) => [
        (data.required)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createTextVNode(_toDisplayString(_unref(t)("common.labels.yes")), 1)
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createTextVNode(_toDisplayString(_unref(t)("common.labels.not")), 1)
            ], 64))
      ]),
      _: 1
    }, 8, ["header", "data", "loading", "ordering"]),
    _createVNode(_unref(baseModal), {
      width: "80%",
      show: showQuestionAdd.value,
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.confirm'),
      "button-close": _unref(t)('common.labels.cancel'),
      title: modalMessage.value,
      onClose: _cache[4] || (_cache[4] = ($event: any) => (addCancel())),
      onConfirm: _cache[5] || (_cache[5] = ($event: any) => (questionSubmit()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(questionForm), {
          width: "100%",
          data: questionPayload.value,
          remove: __props.remove,
          onUpdateData: _cache[3] || (_cache[3] = ($event: any) => (questionPayload.value = $event))
        }, null, 8, ["data", "remove"])
      ]),
      _: 1
    }, 8, ["show", "button-confirm", "button-close", "title"]),
    _createVNode(_unref(baseConfirm), {
      "max-width": "500px",
      show: showQuestionDel.value,
      title: _unref(t)('question.texts.del_question').toUpperCase(),
      message: delMessage.value,
      onCancel: _cache[6] || (_cache[6] = ($event: any) => (delCancel())),
      onConfirm: _cache[7] || (_cache[7] = ($event: any) => (delConfirm()))
    }, null, 8, ["show", "title", "message"])
  ]))
}
}

})